import React, { useEffect, useRef, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { CampLocation } from "@/types/campLocation";
import { OfferFormDialog } from "./OfferFormDialog";
import { SessionsViewToggle } from "./SessionsViewToggle";
import { SessionsHeader } from "./SessionsHeader";
import { SessionsContent } from "./SessionsContent";
import { useSessionsTab } from "./hooks/useSessionsTab";
import { db } from "@/services/firebaseService";
import { getCampLocationById } from "@/services/campLocationService";
import { BulkAddSessionsModal } from "./BulkAddSessionsModal";
import { useBulkSessionsCreation } from "./hooks/useBulkSessionsCreation";
import { BulkAddSessionsFormValues } from "./schemas/bulkAddSessionsSchema";
import { useAuth } from "@/context/AuthContext";
import { AISessionFetchModal } from "@/features/sessions/components/AISessionFetchModal";
import { enqueueBulkSessionJob } from "@/services/bulkSessionJobsService";
import {
  bulkDeleteFutureOffersForLocation,
  bulkDeleteFutureOffersForOrganization,
} from "@/services/offer/offerBulkDeleteService";
import { toast } from "sonner";
import { CleanSessionsModal } from "./CleanSessionsModal";
import { arrayUnion, doc, getDoc, onSnapshot, setDoc } from "firebase/firestore";
import { extractLocationId } from "@/services/offer/offerCopyHelpers";
import { CampDataRunDocV1Schema } from "@/types/campData";
import { ensureRowFormatPrefillWithWrappedCurlyHeader } from "@/components/admin/camp/camp-data/utils/orgBulkPrefill";

const dummyLocation = {
  id: "",
  name: "",
  visibility: "v_draft",
  variations: [],
} as unknown as CampLocation;

export interface SessionsTabProps {
  /** Location mode: single location (location edit page). */
  location?: CampLocation;
  locationId?: string;
  /** Org mode: sessions across all org locations (organization edit page). */
  organizationId?: string | null;
  isInvitedUser?: boolean;
  onProgramSaved?: (locationId: string) => Promise<void>;
  isWelcomeScreen?: boolean;
}

export const SessionsTab: React.FC<SessionsTabProps> = ({
  location,
  locationId,
  organizationId,
  isInvitedUser = false,
  onProgramSaved,
  isWelcomeScreen = false
}) => {
  const isOrgMode = organizationId != null && organizationId !== "";
  const [searchParams] = useSearchParams();
  const [directPublishedLocation, setDirectPublishedLocation] = useState<CampLocation | null>(null);
  const [isBulkSessionsModalOpen, setIsBulkSessionsModalOpen] = useState(false);
  const [isAIFetchModalOpen, setIsAIFetchModalOpen] = useState(false);
  const [isCleanModalOpen, setIsCleanModalOpen] = useState(false);
  const [isCleanDoingIt, setIsCleanDoingIt] = useState(false);
  const [savedSessionFormData, setSavedSessionFormData] = useState<any>(null);
  const [savedAddAnotherChecked, setSavedAddAnotherChecked] = useState<boolean>(false);
  const [bulkModalMode, setBulkModalMode] = useState<"location" | "org">("location");
  const [bulkInitialDateListInput, setBulkInitialDateListInput] = useState<string | undefined>(undefined);
  const sessionAddAnotherClearTimerRef = useRef<number | null>(null);
  const bulkJobUnsubRef = useRef<null | (() => void)>(null);
  const { currentUser } = useAuth();
  const orgBulkAutoOpenHandledRef = useRef(false);

  const {
    viewMode,
    setViewMode,
    dialogKey,
    isLoading,
    offers,
    isDialogOpen,
    currentOffer,
    selectedDate,
    isCopyOperation,
    isAdmin,
    isPublishing,
    calendarProgramFilter,
    setCalendarProgramFilter,
    tableDateFilter,
    setTableDateFilter,
    lastOfferByVariation,
    availabilityStatusOptions,
    signupStatusOptions,
    filteredCalendarEvents,
    filteredTableOffers,
    handleOpenDialog,
    handleCloseDialog,
    handleSaveOffer,
    handleDayClick,
    handleEditSession,
    handleAddSession,
    handleDeleteOffer,
    handleCopyOffer,
    handlePublishToOffers,
    handleBulkUpdateOffers,
    refreshOffers,
    publishedLocation,
    isOrgMode: hookIsOrgMode,
    locations: orgLocations,
    calendarBaseMonth,
    setCalendarBaseMonth,
    calendarLegendVariations,
  } = useSessionsTab(
    isOrgMode
      ? { organizationId }
      : { locationId: locationId ?? "", location: location ?? dummyLocation }
  );

  const locationForBulkCreation = isOrgMode ? dummyLocation : (location ?? dummyLocation);
  const locationIdForBulkCreation = isOrgMode ? "" : (locationId ?? "");
  useBulkSessionsCreation({
    locationId: locationIdForBulkCreation,
    location: locationForBulkCreation,
    userId: currentUser?.uid,
    onSuccess: refreshOffers,
  });

  useEffect(() => {
    if (isOrgMode || !locationId || !location) return;
    const fetchPublishedLocation = async () => {
      try {
        const fetchedLocation = await getCampLocationById(db, locationId);
        if (fetchedLocation?.visibility === "v_published") {
          setDirectPublishedLocation(fetchedLocation);
        } else {
          setDirectPublishedLocation(null);
        }
      } catch (error) {
        console.error("[SessionsTab] Error fetching published location", error);
        setDirectPublishedLocation(null);
      }
    };
    if (location.visibility !== "v_published") {
      fetchPublishedLocation();
    } else {
      setDirectPublishedLocation(location);
    }
  }, [isOrgMode, locationId, location]);

  const handleSaveOfferWithAutoPublish = async (...args: Parameters<typeof handleSaveOffer>) => {
    await handleSaveOffer(...args);
  };

  const locationForDialog = directPublishedLocation || publishedLocation || (location ?? dummyLocation);

  const tableColumnOptions = useMemo(() => {
    if (!hookIsOrgMode || !orgLocations?.length) return undefined;
    return {
      showLocationColumn: true,
      getLocationName: (offer: { locationRef?: unknown }) => {
        const locId = extractLocationId(offer as any);
        return orgLocations.find((l) => l.id === locId)?.name ?? "–";
      },
      showOrgExtraColumns: true,
    };
  }, [hookIsOrgMode, orgLocations]);

  const sessionsCalendarVariations = useMemo(() => {
    if (hookIsOrgMode && calendarLegendVariations.length > 0) {
      return calendarLegendVariations;
    }
    return (location ?? dummyLocation).variations ?? [];
  }, [hookIsOrgMode, calendarLegendVariations, location]);

  // Handle bulk add sessions
  const handleBulkAddSessions = () => {
    setBulkModalMode("location");
    setBulkInitialDateListInput(undefined);
    setIsBulkSessionsModalOpen(true);
  };

  // Deep-link org-mode bulk add from /camp-data: ?tab=sessions&orgBulk=1&campDataRunId=<runDocId>
  // Prefill comes from the run doc's reconcile data in Firestore (cloud assets).
  useEffect(() => {
    if (isOrgMode) return;
    if (!locationId) return;

    const orgBulk = String(searchParams.get("orgBulk") || "").trim();
    const runDocId = String(searchParams.get("campDataRunId") || "").trim();
    if (orgBulk !== "1" || !runDocId) return;
    if (orgBulkAutoOpenHandledRef.current) return;

    orgBulkAutoOpenHandledRef.current = true;
    setBulkModalMode("org");

    let cancelled = false;
    (async () => {
      try {
        const runSnap = await getDoc(doc(db, "campDataRuns", runDocId));
        if (cancelled) return;
        const raw = runSnap.exists() ? runSnap.data() : null;
        const parsed = raw ? CampDataRunDocV1Schema.safeParse(raw) : null;
        const data = parsed?.success ? parsed.data : null;
        const finalized = String(data?.reconcile?.finalizedOutputText || "").trim();
        const missingRowFormat = String(data?.reconcile?.missingRowFormatText || "").trim();
        const base = finalized || missingRowFormat;
        const prefill = base ? ensureRowFormatPrefillWithWrappedCurlyHeader(base) : "RowFormat: ";
        setBulkInitialDateListInput(prefill);
      } catch (e) {
        if (!cancelled) {
          console.warn("[SessionsTab] Failed to load orgBulk prefill from Firestore", e);
          setBulkInitialDateListInput("RowFormat: ");
        }
      }
      if (!cancelled) setIsBulkSessionsModalOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOrgMode, locationId, searchParams]);

  // Handler for AI-powered session fetch
  const handleAIFetch = () => {
    setIsAIFetchModalOpen(true);
  };

  // Handler to close AI fetch modal
  const handleCloseAIFetchModal = () => {
    setIsAIFetchModalOpen(false);
  };

  const handleCleanSessions = () => {
    setIsCleanModalOpen(true);
  };

  const handleCleanConfirm = async (choice: "nothing" | "delete_future") => {
    if (choice === "nothing") return;
    setIsCleanDoingIt(true);
    try {
      const result = isOrgMode && organizationId
        ? await bulkDeleteFutureOffersForOrganization(organizationId)
        : locationId
          ? await bulkDeleteFutureOffersForLocation(locationId)
          : { success: false as const, error: "Missing location or organization" };
      if (result.success) {
        toast.success(`Deleted ${result.deleted ?? 0} future sessions`);
        await refreshOffers();
      } else {
        toast.error(result.error ?? "Failed to delete future sessions");
      }
    } finally {
      setIsCleanDoingIt(false);
    }
  };
  
  // Handle bulk sessions form submission
  const handleBulkSessionsSubmit = async (
    formValues: BulkAddSessionsFormValues,
    generatedDates: Date[]
  ) => {
    try {
      if (!currentUser?.uid) {
        toast.error("You must be signed in to create sessions");
        return;
      }
      if (!locationId) {
        toast.error("Missing location");
        return;
      }

      // Enqueue background job and close modal immediately
      const { jobId } = await enqueueBulkSessionJob(db, currentUser.uid, locationId, formValues, generatedDates);
      toast.success(`Bulk creation started (${generatedDates.length} sessions). Tracking job ${jobId}…`);

      // If this was launched from /camp-data orgBulk shortcut, record action on the run doc.
      try {
        const orgBulk = String(searchParams.get("orgBulk") || "").trim();
        const runDocId = String(searchParams.get("campDataRunId") || "").trim();
        if (bulkModalMode === "org" && orgBulk === "1" && runDocId) {
          const ref = doc(db as any, "campDataRuns", runDocId);
          await setDoc(
            ref as any,
            {
              operatorActions: arrayUnion({
                kind: "orgBulk.enqueued",
                atIso: new Date().toISOString().replace(/\.\d{3}Z$/, "Z"),
                userId: currentUser.uid,
                meta: {
                  jobId,
                  baseLocationId: locationId,
                  parsedRows: generatedDates.length,
                },
              }),
            } as any,
            { merge: true }
          );
        }
      } catch (e) {
        console.warn("[SessionsTab] Failed to record orgBulk operator action", e);
      }

      // Close the modal and refresh after completion via listener if desired
      setIsBulkSessionsModalOpen(false);

      // Fire-and-forget listener to toast a completion summary and refresh offers.
      // (Unsubscribes itself after completion/failure.)
      try {
        if (bulkJobUnsubRef.current) {
          bulkJobUnsubRef.current();
          bulkJobUnsubRef.current = null;
        }
        const jobDocRef = doc(db as any, "bulkSessionJobs", jobId);
        bulkJobUnsubRef.current = onSnapshot(jobDocRef as any, async (snap) => {
          if (!snap.exists()) return;
          const data = snap.data() as any;
          const status = String(data?.status || "");
          if (status !== "completed" && status !== "failed") return;

          const total = Number(data?.total ?? generatedDates.length) || generatedDates.length;
          // `created` is createdRows (1 per input row); createdOffers may be higher when single-day overlaps are enabled.
          const created = Number(data?.created ?? 0);
          const skippedUnknownLocationId = Number(data?.skippedUnknownLocationId ?? 0);
          const failed = Number(data?.failed ?? 0);

          if (status === "completed") {
            if (skippedUnknownLocationId > 0) {
              toast.success(`Bulk creation completed: created ${created} of ${total} sessions (skipped ${skippedUnknownLocationId} unknown locationId).`);
            } else {
              toast.success(`Bulk creation completed: created ${created} sessions.`);
            }
            await refreshOffers();
          } else {
            toast.error(data?.error || "Bulk creation failed.");
          }

          // If this was launched from /camp-data orgBulk shortcut, record completion on the run doc.
          try {
            const orgBulk = String(searchParams.get("orgBulk") || "").trim();
            const runDocId = String(searchParams.get("campDataRunId") || "").trim();
            if (bulkModalMode === "org" && orgBulk === "1" && runDocId) {
              const ref = doc(db as any, "campDataRuns", runDocId);
              await setDoc(
                ref as any,
                {
                  operatorActions: arrayUnion({
                    kind: status === "completed" ? "orgBulk.completed" : "orgBulk.failed",
                    atIso: new Date().toISOString().replace(/\.\d{3}Z$/, "Z"),
                    userId: currentUser?.uid || undefined,
                    meta: {
                      jobId,
                      status,
                      total,
                      created,
                      failed,
                      skippedUnknownLocationId,
                    },
                  }),
                } as any,
                { merge: true }
              );
            }
          } catch (e) {
            console.warn("[SessionsTab] Failed to record orgBulk completion operator action", e);
          }

          if (bulkJobUnsubRef.current) {
            bulkJobUnsubRef.current();
            bulkJobUnsubRef.current = null;
          }
        });
      } catch (listenErr: any) {
        console.warn("[SessionsTab] Failed to attach bulk job listener", listenErr);
      }
    } catch (e: any) {
      console.error("Failed to enqueue bulk job", e);
      toast.error(e?.message || "Failed to start background job");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (bulkJobUnsubRef.current) {
        bulkJobUnsubRef.current();
        bulkJobUnsubRef.current = null;
      }
    };
  }, []);
  
  // Listen for "Add another" events from bulk sessions modal
  useEffect(() => {
    const handleAddAnother = (_event: CustomEvent) => {
      // Reopen the modal after a short delay
      setTimeout(() => {
        setIsBulkSessionsModalOpen(true);
      }, 150);
    };
    
    window.addEventListener('bulkSessionsAddAnother', handleAddAnother as EventListener);
    
    return () => {
      window.removeEventListener('bulkSessionsAddAnother', handleAddAnother as EventListener);
    };
  }, []);
  
  // Listen for "Add another" events from session modal
  useEffect(() => {
    const handleSessionAddAnother = (event: CustomEvent) => {
      // Cancel any pending clear
      if (sessionAddAnotherClearTimerRef.current) {
        clearTimeout(sessionAddAnotherClearTimerRef.current);
        sessionAddAnotherClearTimerRef.current = null;
      }
      // Save the form data and checkbox state for next modal opening
      if (event.detail?.formData) {
        setSavedSessionFormData(event.detail.formData);
      }
      if (typeof event.detail?.addAnotherChecked === 'boolean') {
        setSavedAddAnotherChecked(event.detail.addAnotherChecked);
      }
      // Clear current offer and reopen modal after allowing close cleanup to complete
      setTimeout(() => {
        handleAddSession();
      }, 400);
    };
    
    window.addEventListener('sessionAddAnother', handleSessionAddAnother as EventListener);
    
    return () => {
      window.removeEventListener('sessionAddAnother', handleSessionAddAnother as EventListener);
    };
  }, [handleAddSession]);

  // If dialog closes and no add-another event arrives, clear the saved data slightly later
  useEffect(() => {
    if (!isDialogOpen) {
      if (sessionAddAnotherClearTimerRef.current) {
        clearTimeout(sessionAddAnotherClearTimerRef.current);
      }
      sessionAddAnotherClearTimerRef.current = window.setTimeout(() => {
        setSavedSessionFormData(null);
        setSavedAddAnotherChecked(false);
        sessionAddAnotherClearTimerRef.current = null;
      }, 600);
    }
  }, [isDialogOpen]);
  
  // Deep-link: open specific offer dialog via ?openOfferId=... and optionally switch to sessions via parent
  useEffect(() => {
    if (isLoading) return;
    const openOfferId = searchParams.get("openOfferId");
    if (openOfferId) {
      const target = offers.find(o => o.id === openOfferId);
      if (target) {
        handleOpenDialog(target, false);
      }
      // Clean the param so it doesn't re-trigger
      const url = new URL(window.location.href);
      url.searchParams.delete('openOfferId');
      window.history.replaceState({}, '', url.toString());
    }
  }, [isLoading, offers, searchParams, handleOpenDialog]);
  
  return (
    <div className="space-y-6">
      <SessionsHeader isInvitedUser={isInvitedUser} isWelcomeScreen={isWelcomeScreen} />
      
      <SessionsViewToggle
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onAddSession={handleAddSession}
        onBulkAddSessions={handleBulkAddSessions}
        onRefresh={refreshOffers}
        onPublishToOffers={handlePublishToOffers}
        onAIFetch={isWelcomeScreen ? undefined : handleAIFetch}
        onCleanSessions={handleCleanSessions}
        isAdmin={isAdmin}
        isPublishing={isPublishing}
        isInvitedUser={isInvitedUser}
        hideActionButtons={hookIsOrgMode}
        showCleanButton={hookIsOrgMode}
      />

      <SessionsContent
        viewMode={viewMode}
        isLoading={isLoading}
        offersLength={offers.length}
        filteredCalendarEvents={filteredCalendarEvents}
        filteredTableOffers={filteredTableOffers}
        variations={sessionsCalendarVariations}
        calendarProgramFilter={calendarProgramFilter}
        tableDateFilter={tableDateFilter}
        onAddSession={handleAddSession}
        onDayClick={hookIsOrgMode ? undefined : handleDayClick}
        onFilterChange={setCalendarProgramFilter}
        onEditSession={handleEditSession}
        onEditOffer={handleOpenDialog}
        onDeleteOffer={handleDeleteOffer}
        onCopyOffer={handleCopyOffer}
        onBulkUpdateOffers={handleBulkUpdateOffers}
        onDateFilterChange={setTableDateFilter}
        availabilityStatusOptions={availabilityStatusOptions}
        signupStatusOptions={signupStatusOptions}
        onRefreshOffers={refreshOffers}
        isAdmin={isAdmin}
        tableColumnOptions={tableColumnOptions}
        calendarBaseMonth={hookIsOrgMode ? calendarBaseMonth : undefined}
        onCalendarBaseMonthChange={
          hookIsOrgMode ? setCalendarBaseMonth : undefined
        }
      />
      
      {/* Dialog for adding/editing offers with delete handler added */}
      {/* Use a key without dialogKey to prevent complete remounting */}
      <OfferFormDialog 
        key={`offer-dialog-${dialogKey}`} 
        isOpen={isDialogOpen} 
        onClose={() => {
          // Do not clear immediately; delayed clear handled by effect to avoid race with add-another
          handleCloseDialog();
        }}
        onSave={isInvitedUser ? handleSaveOfferWithAutoPublish : handleSaveOffer} 
        onDelete={handleDeleteOffer}
        initialDate={selectedDate || undefined}
        location={locationForDialog ?? dummyLocation}
        initialOffer={currentOffer} 
        lastOfferByVariation={lastOfferByVariation} 
        availabilityStatusOptions={availabilityStatusOptions} 
        signupStatusOptions={signupStatusOptions}
        isCopyOperation={isCopyOperation}
        savedFormData={savedSessionFormData}
        savedAddAnotherChecked={savedAddAnotherChecked}
      />
      
      {/* Bulk Add Sessions Modal - now passing status options */}
      <BulkAddSessionsModal
        isOpen={isBulkSessionsModalOpen}
        onClose={() => setIsBulkSessionsModalOpen(false)}
        location={location ?? dummyLocation}
        onSubmit={handleBulkSessionsSubmit}
        availabilityStatusOptions={availabilityStatusOptions}
        signupStatusOptions={signupStatusOptions}
        mode={bulkModalMode}
        initialDateListInput={bulkInitialDateListInput}
      />

      {/* AI Session Fetch Modal */}
      <AISessionFetchModal
        isOpen={isAIFetchModalOpen}
        onClose={handleCloseAIFetchModal}
        location={locationForDialog ?? dummyLocation}
        locationId={locationId ?? ""}
      />

      <CleanSessionsModal
        isOpen={isCleanModalOpen}
        onClose={() => setIsCleanModalOpen(false)}
        onConfirm={handleCleanConfirm}
        isDoingIt={isCleanDoingIt}
      />
    </div>
  );
};
